
import { LINK_CLASS, StatusChip } from '@league/ui';
import { Link, useOutletContext } from 'react-router-dom';
import { PrintScoreSheets } from '../../seating/PrintScoreSheets';
import { ShowScoreQr } from '../../seating/ShowScoreQr';

import { leagueDate } from '../../../lib/when';
import { scoreState } from '../session-state';
import type { LeagueContext } from '../LeagueWorkspace';

/**
 * A list of sessions, each linking to its score sheet — the same shape as the Seating tab, because
 * scoring is per session for the same reason seating is.
 *
 * The sheet itself is at `/director/sessions/:id/scores` and is built for a phone: she enters it
 * standing up in the hall, not at a desk afterwards.
 */
export function ScoresTab() {
  const { league, sessions } = useOutletContext<LeagueContext>();

  return (
    <section className="flex flex-col gap-3">
      {/* The league's one score sheet, for every week and every table (2026-10-15). */}
      <div className="flex flex-wrap justify-end gap-2">
        <PrintScoreSheets leagueId={league.id} variant="button" />
        <ShowScoreQr leagueId={league.id} />
      </div>

      {sessions.length === 0 ? (
        /*
          A league always gets its sessions when it is created, so this should not happen — which is
          exactly why it is here. An empty bordered box reads as something broken; a sentence reads
          as a state, and points at the one screen that can fix it.
        */
        <p className="rounded-md border border-dashed border-light px-5 py-10 text-center text-[14px] text-ink-3">
          No sessions are scheduled for this league.
        </p>
      ) : (
        <ol className="flex flex-col divide-y divide-light rounded-md border border-light bg-paper">
          {sessions.map((session) => (
            <li key={session.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
              <div className="flex min-w-0 flex-wrap items-baseline gap-3">
                <span className="tabular text-[13px] text-ink-3">{session.sessionNumber}</span>
                <span className="font-medium">
                  {leagueDate(session.startsAt, league.timeZone, {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              </div>
              <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
                <StatusChip status={scoreState(session)} />
                <Link
                  to={`/director/sessions/${session.id}/${session.seating === 'published' || session.scoresClosedAt || session.scoresEntered > 0 ? 'scores' : 'seating'}`}
                  className={`text-[13px] ${LINK_CLASS}`}
                >
                  {session.scoresApproved || session.scoresClosedAt || (session.seating !== 'published' && session.scoresEntered > 0) ? 'View scores' : session.seating === 'published' ? 'Enter scores' : 'Finalize seating to enter scores'}
                </Link>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

